<template>
    <div class="mt-12 flex flex-wrap items-start gap-6">
        <div class="flex h-10 min-w-[160px] flex-col text-base leading-normal">
            <p class="text-gray-500">Active users · {{ timeframeLabel }}</p>

            <div v-if="loading" class="mb-[3px] mt-0.5 flex">
                <LoadingSpinner size="small" />
            </div>

            <p
                v-else
                class="font-medium"
                :class="{
                    'text-green-600': !uniqueUsers.usersCountExceeded,
                    'text-red-600': uniqueUsers.usersCountExceeded,
                }"
            >
                {{ uniqueUsers.usersCount }}

                <span
                    v-if="uniqueUsers.maxUsers !== 999999999"
                    class="text-gray-800"
                >
                    / {{ uniqueUsers.maxUsers }}</span
                >
                <span v-else class="text-gray-800"> / ∞</span>
            </p>
        </div>

        <div
            v-if="billingPeriod"
            class="flex h-10 min-w-[160px] flex-col text-base leading-normal"
        >
            <p class="text-gray-500">
                Billed users ·
                {{
                    billingPeriod.source === 'stripe'
                        ? 'Stripe period'
                        : 'local estimate'
                }}
                {{ formatPeriodDate(billingPeriod.from) }} –
                {{ formatPeriodDate(billingPeriod.to) }}
            </p>

            <p
                class="font-medium"
                :class="{
                    'text-green-600': !billingPeriod.usersCountExceeded,
                    'text-red-600': billingPeriod.usersCountExceeded,
                }"
            >
                {{ billingPeriod.usersCount }}

                <span
                    v-if="billingPeriod.maxUsers !== 999999999"
                    class="text-gray-800"
                >
                    / {{ billingPeriod.maxUsers }}</span
                >
                <span v-else class="text-gray-800"> / ∞</span>
            </p>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import axios from 'axios';
import { format } from 'date-fns';
import { route } from 'ziggy-js';
import LoadingSpinner from '@/components/Atomic/LoadingSpinner/LoadingSpinner.vue';
import IPluginUniqueUsers from '@/types/Plugin/IPluginUniqueUsers';
import IPluginBillingPeriodUniqueUsers from '@/types/Plugin/IPluginBillingPeriodUniqueUsers';

const props = defineProps<{
    params: { user?: number; team?: number };
}>();

const uniqueUsers = ref({} as IPluginUniqueUsers);
const billingPeriod = ref<IPluginBillingPeriodUniqueUsers | null>(null);
const loading = ref(true);
const timeframeLabel = ref('This month');

const formatPeriodDate = (date: string) =>
    format(new Date(date), 'd. L. yyyy');

onMounted(async () => {
    const response = await axios.get(
        route('kiosk.api-tokens.unique-users.index', props.params)
    );

    uniqueUsers.value = response.data.data;
    billingPeriod.value = response.data.billingPeriod;
    timeframeLabel.value = response.data.timeframeLabel;
    loading.value = false;
});
</script>
